import {
  ECardinality,
  ESymbolCategory,
  type IActionDefinitionDto,
  type IDisplayItemProperty,
  ISymbol
} from "@kuika/kuika-cl-model";
import {
  enrichActionFieldSymbol,
  findActionDefinitionByResultActionId,
  findTargetPropertyShape
} from "../../bindables/action-field-enricher";
import { decodeSymbolRef } from "../../bindables/symbol-ref";
import {
  isActionDefBinding,
  resolveSymbolFromLiteral,
  resolveSymbolFromRef,
  SymbolResolutionError
} from "../../bindables/symbol-resolver";
import { getValues } from "../../designer-context";
import {
  buildBindablesManifest,
  enrichTableFieldBindingSymbol,
  listDesignerMicroAppProjectScreens,
  listDesignerMicroAppProjects,
  listDesignerMicroAppScreenInputs,
  listDesignerBindingOptions,
  resolveDefaultValueName,
  setDesignerMicroAppProject,
  setDesignerMicroAppStartingScreen,
  setDesignerPropertySymbol,
  setDesignerPropertyType,
  setDesignerPropertyTypeOptions,
  setDesignerPropertyBinding,
  updateDesignerPropertySymbol,
  validateBindablesNarrowing
} from "../../designer-property-actions";
import type {
  IBindablesManifestInput,
  IBindablesManifestTarget,
  IResolvedDefaultValue,
  ISetPropertySymbolInput,
  IUpdatePropertySymbolInput,
  ISetPropertyTypeInput,
  ISetPropertyTypeOptionsInput,
  ISetMicroAppProjectInput,
  ISetMicroAppStartingScreenInput
} from "../../designer-property-actions";
import {
  getDesignerPropertyDataEditorState,
  setDesignerPropertyDataAction,
  setDesignerPropertyDataField
} from "../../designer-property-data-editor-actions";
import type {
  DesignerPropertyDataEditorOption,
  DesignerPropertyDataFieldEditor
} from "../../designer-property-data-editor-actions";
import { getScreenDictionary } from "../../designer-utils";
import { getSdInstance, resolveKuikaIdRef } from "../helpers";
import type { ToolHandlerGroup } from "../types";
import { actionTools, autoResolveKuikaIdField, getAllActionDefinitions, resolveFriendlyKuikaId } from "./action-tools";

function safeParse(val: any): any {
  if (val == null) return val;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
  return val;
}

interface IDatasourceCellBinding {
  bindingType?: string;
  kuikaId?: string | number;
  propertyName?: string;
}

/**
 * KOA-17797 — After `set_multiple_bindings` applies the flat `actionField` symbols
 * for a datasource's row columns, upgrade each in place to the deterministic
 * field-of-type shape the human designer's column picker produces (see
 * `enrichTableFieldBindingSymbol`): real `fieldToDisplay` / `fieldType` /
 * `fixedValue: "[datafield:...]"` / `tableLabelSourceType`, and corrected field
 * casing (`UserName` -> `Username`). Preserves the symbol's already-resolved
 * `label`/`currentType`/`targetType`. Conservative: a column whose field can't be
 * resolved against the table's datasource is left as the flat binding.
 */
function enrichDatasourceColumnBindings(bindings: IDatasourceCellBinding[]): void {
  for (const b of bindings) {
    const cellKuikaId: string | number | undefined = b.kuikaId;
    const cellProp: string | undefined = b.propertyName;
    if (b.bindingType !== "actionField" || cellKuikaId === undefined || cellKuikaId === null || !cellProp) {
      continue;
    }
    const sd = getValues(getSdInstance());
    const dict = getScreenDictionary(sd);
    const di = dict[parseInt(String(cellKuikaId), 10)];
    const current: ISymbol | undefined = di?.item?.properties?.find(
      (p: IDisplayItemProperty) => p.name === cellProp
    )?.symbol;
    if (!current || current.category !== ESymbolCategory.actionField) continue;
    const enriched: ISymbol = enrichTableFieldBindingSymbol(current, getSdInstance(), cellKuikaId);
    if (enriched !== current) {
      setDesignerPropertySymbol(getSdInstance(), {
        kuikaId: String(cellKuikaId),
        propertyName: cellProp,
        symbol: enriched
      });
    }
  }
}

/**
 * Fix #34 — Rewrite `componentValue:<friendlyName>[:propName]` symbolRefs
 * into the canonical `di:<numericKuikaId>:<propName>` shape before any
 * decoder runs.
 *
 * Background
 * ----------
 * The Binder LLM, primed by the BRAID intent `bindingType: "componentValue"`
 * on parameters, sometimes emits the same shorthand back into ui_bind.apply
 * as `symbolRef: "componentValue:<input-title>"`. The `decodeSymbolRef`
 * decoder has no `componentValue` kind — it switches on `default`,
 * `screenInput`, `di`, `actionResult`, `actionField`, `actionDef`,
 * `current`, `bag` — and throws `symbolRef: unknown kind "componentValue"`,
 * which the Binder then retries identically, burning turns.
 *
 * Resolution
 * ----------
 * Translate the shorthand transparently: `componentValue:<friendly>`
 * becomes `di:<resolvedId>:value` (the default prop for input
 * components); `componentValue:<friendly>:<propName>` becomes
 * `di:<resolvedId>:<propName>`. If the friendlyName can't be resolved
 * uniquely, leave the ref alone so the existing "unknown kind" error
 * keeps the LLM honest (mirrors `resolveFriendlyKuikaId`'s policy).
 */
const COMPONENT_VALUE_COLON_PREFIX = "componentValue:";
const COMPONENT_VALUE_DOT_PREFIX = "componentValue.";
const DEFAULT_COMPONENT_VALUE_PROP = "value";

export function rewriteComponentValueSymbolRef(symbolRef: unknown, sd: unknown): string | undefined {
  if (typeof symbolRef !== "string") return undefined;
  // KOA-17394 (Gap #3): accept BOTH the canonical colon form
  // `componentValue:<friendly>[:propName]` AND the BRAID-notation dot
  // form `componentValue.<friendly>` (and the rare hybrid
  // `componentValue.<friendly>:<propName>`). The Binder LLM consistently
  // transcribes the BRAID `Bind X.Y from componentValue.<inputId>` line
  // verbatim into ui_bind.apply, and the original prefix-by-colon-only
  // path then handed the dot form to decodeSymbolRef which rejected it
  // as `symbolRef: unknown kind "componentValue.<id>"`. The LLM then
  // retried the same shape, burning 1-2 turns every Binder run. By
  // normalising the dot form here before the decoder sees it, both
  // notations resolve to `di:<numericId>:<propName>`.
  let tail: string;
  if (symbolRef.startsWith(COMPONENT_VALUE_COLON_PREFIX)) {
    tail = symbolRef.slice(COMPONENT_VALUE_COLON_PREFIX.length);
  } else if (symbolRef.startsWith(COMPONENT_VALUE_DOT_PREFIX)) {
    tail = symbolRef.slice(COMPONENT_VALUE_DOT_PREFIX.length);
  } else {
    return undefined;
  }
  if (tail.length === 0) return undefined;
  // The friendly/id segment ends at the FIRST separator — colon takes
  // precedence over the (less canonical) trailing dot, but we accept
  // either as the propName divider so `componentValue.input-x.text` and
  // `componentValue.input-x:text` both produce the same di-ref.
  const colonIdx: number = tail.indexOf(":");
  const dotIdx: number = tail.indexOf(".");
  let splitIdx: number;
  if (colonIdx === -1) splitIdx = dotIdx;
  else if (dotIdx === -1) splitIdx = colonIdx;
  else splitIdx = Math.min(colonIdx, dotIdx);

  const friendlyOrId: string = splitIdx === -1 ? tail : tail.slice(0, splitIdx);
  const propName: string =
    splitIdx === -1 || splitIdx === tail.length - 1 ? DEFAULT_COMPONENT_VALUE_PROP : tail.slice(splitIdx + 1);
  const resolved: string | undefined = resolveFriendlyKuikaId(friendlyOrId, sd);
  const finalId: string = resolved ?? friendlyOrId;
  // Not a numeric kuikaId after resolution — friendlyName was ambiguous
  // or genuinely unknown. Bail so the caller sees the existing
  // "unknown kind" decoder error instead of silently producing a
  // `di:<friendly>:value` that the symbol resolver would then reject.
  if (!/^-?\d+$/.test(finalId)) return undefined;
  return `di:${finalId}:${propName}`;
}

/**
 * Turns a `ui_bind` event-parameters object ({Name: {symbolRef}|{literal}})
 * into the legacy parameter-array shape `add_action_to_event` expects:
 *   [{name, bindingType, sourceKuikaId, sourcePropertyName, ...}]
 * Unknown refs / missing values become literal fixed-value parameters so the
 * existing add_action path can still create the action; the returned binding
 * result will carry whatever error the action tool raises.
 */
function translateEventParameters(rawParameters: unknown): unknown[] {
  const parsed = safeParse(rawParameters);
  if (!parsed || typeof parsed !== "object") return [];

  const out: Record<string, unknown>[] = [];
  for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
    const entry: Record<string, unknown> = { name };
    const wrapper = value && typeof value === "object" ? (value as Record<string, unknown>) : null;

    if (wrapper && typeof wrapper.symbolRef === "string") {
      // Fix #34 — rewrite `componentValue:<friendlyName>` shorthand here too.
      const rewritten: string | undefined = rewriteComponentValueSymbolRef(wrapper.symbolRef, getSdInstance());
      const symbolRef: string = rewritten ?? wrapper.symbolRef;
      if (rewritten !== undefined) {
        wrapper.symbolRef = rewritten;
      }
      try {
        const decoded = decodeSymbolRef(symbolRef);
        switch (decoded.kind) {
          case "screenInput":
            entry.bindingType = "screenInput";
            entry.inputName = decoded.name;
            break;
          case "default":
            entry.bindingType = "default";
            entry.defaultName = decoded.name;
            break;
          case "di":
            entry.bindingType = "componentValue";
            entry.sourceKuikaId = decoded.kuikaId;
            entry.sourcePropertyName = decoded.propName;
            break;
          case "actionResult":
            entry.bindingType = "actionResult";
            entry.actionId = decoded.resultActionId;
            break;
          case "actionField":
            entry.bindingType = "actionField";
            entry.actionId = decoded.resultActionId;
            entry.fieldName = decoded.fieldPath;
            break;
          case "current":
            entry.bindingType = "actionField";
            entry.fieldName = decoded.fieldPath;
            break;
          default:
            entry.value = "";
            break;
        }
      } catch {
        entry.value = "";
      }
    } else if (wrapper && "literal" in wrapper) {
      entry.value = wrapper.literal;
    } else {
      entry.value = value;
    }

    out.push(entry);
  }
  return out;
}

/** One datasource field mapping `set_datasource_bindings` writes after the action is bound. */
interface IDatasourceFieldWrite {
  /** Where the caller put it — `dataTextField`, `chartRoles[<key>]`, … — so a warning names their input. */
  source: string;
  /** Data-editor key the value is written under. */
  key: string;
  value: string;
}

/** A `{ key, value }` entry of `datasource.chartRoles` / `datasource.fieldMaps`. */
interface IKeyedFieldEntry {
  key?: unknown;
  value?: unknown;
}

function collectKeyedFieldWrites(listName: string, entries: unknown): IDatasourceFieldWrite[] {
  if (!Array.isArray(entries)) return [];
  const writes: IDatasourceFieldWrite[] = [];
  for (const entry of entries as IKeyedFieldEntry[]) {
    if (!entry?.key || entry.value == null || entry.value === "") continue;
    const key: string = String(entry.key);
    writes.push({ source: `${listName}[${key}]`, key, value: String(entry.value) });
  }
  return writes;
}

/**
 * The names the data editor offers for `key` on this property — i.e. the bound
 * action's real result fields — or `[]` when they are not known (return model
 * not loaded, free-text editor). Empty means "cannot check", never "invalid".
 */
function getKnownFieldNames(editorState: ReturnType<typeof getDesignerPropertyDataEditorState>, key: string): string[] {
  if (!editorState.success) return [];
  const editor: DesignerPropertyDataFieldEditor | undefined = editorState.editors.find(
    (candidate: DesignerPropertyDataFieldEditor): boolean => candidate.key === key
  );
  return (editor?.options ?? []).map((option: DesignerPropertyDataEditorOption): string => option.label);
}

/**
 * Apply the field mappings and return one warning per mapping that did not land
 * cleanly: the write threw, the write returned an error, or the value is not a
 * field the bound action returns. Best-effort by design — never throws.
 */
async function applyDatasourceFieldWrites(
  kuikaId: string,
  propertyName: string,
  writes: IDatasourceFieldWrite[]
): Promise<string[]> {
  const warnings: string[] = [];
  if (writes.length === 0) return warnings;

  let editorState: ReturnType<typeof getDesignerPropertyDataEditorState> | undefined;
  try {
    editorState = getDesignerPropertyDataEditorState(getSdInstance(), { kuikaId, propertyName });
  } catch {
    editorState = undefined;
  }

  for (const write of writes) {
    const knownFields: string[] = editorState ? getKnownFieldNames(editorState, write.key) : [];
    const isKnownField: boolean = knownFields.length === 0 || knownFields.includes(write.value);
    let failure: string | undefined;
    try {
      const result: Awaited<ReturnType<typeof setDesignerPropertyDataField>> = await setDesignerPropertyDataField(
        getSdInstance(),
        { kuikaId, propertyName, key: write.key, value: write.value }
      );
      if (result?.error) failure = result.error;
    } catch (e) {
      failure = e instanceof Error ? e.message : String(e);
    }

    if (failure !== undefined) {
      warnings.push(`${write.source}="${write.value}" was NOT applied (${write.key}): ${failure}`);
    } else if (!isKnownField) {
      warnings.push(
        `${write.source}="${write.value}" was written (${write.key}) but the bound action returns no field with that ` +
          `exact name, so it will resolve to nothing. Result fields: ${knownFields.join(", ")}.`
      );
    }
  }
  return warnings;
}

const DYNAMIC_TYPES_POLL_MS: number = 100;
const DYNAMIC_TYPES_TIMEOUT_MS: number = 5000;

const isDynamicTypesLoading = (): boolean => {
  try {
    return getValues(getSdInstance()).isDynamicTypesLoading === true;
  } catch {
    return false;
  }
};

/**
 * While Bag & Cache dynamic types are in flight the symbol picker shows a
 * loading placeholder instead of those branches, and the manifest walker skips
 * placeholders — so a manifest built mid-load silently lacks them. Wait for the
 * load to settle first. Resolves `true` when settled (immediately if nothing is
 * loading), `false` on timeout.
 */
export function waitForDynamicTypesSettled(timeoutMs: number = DYNAMIC_TYPES_TIMEOUT_MS): Promise<boolean> {
  if (!isDynamicTypesLoading()) return Promise.resolve(true);
  return new Promise((resolve: (settled: boolean) => void): void => {
    let elapsed: number = 0;
    const timer: ReturnType<typeof setInterval> = setInterval((): void => {
      elapsed += DYNAMIC_TYPES_POLL_MS;
      if (!isDynamicTypesLoading()) {
        clearInterval(timer);
        resolve(true);
      } else if (elapsed >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, DYNAMIC_TYPES_POLL_MS);
  });
}

export const propertyTools: ToolHandlerGroup = {
  sync: {
    ui_structure_list_binding_options: (input) => {
      return listDesignerBindingOptions(getSdInstance(), input);
    },

    ui_bindables_get: (input) => {
      const parsedTargets = safeParse(input?.targets);
      if (parsedTargets === undefined || parsedTargets === null) {
        return { error: "targets is required" };
      }
      if (!Array.isArray(parsedTargets) || parsedTargets.length === 0) {
        return { error: "targets must be a non-empty array" };
      }
      // Optional output narrowing — both absent = the full manifest, as before.
      const parsedCategories: unknown = safeParse(input?.categories);
      if (parsedCategories !== undefined && parsedCategories !== null && !Array.isArray(parsedCategories)) {
        return { error: "categories must be an array of category names" };
      }
      const rawLimit: unknown = safeParse(input?.maxOptionsPerCategory);
      const payload: IBindablesManifestInput = { targets: parsedTargets as IBindablesManifestTarget[] };
      if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
        payload.categories = parsedCategories.map((category: unknown): string => String(category));
      }
      if (rawLimit !== undefined && rawLimit !== null && rawLimit !== "") {
        payload.maxOptionsPerCategory = Number(rawLimit);
      }
      const narrowingError: ReturnType<typeof validateBindablesNarrowing> = validateBindablesNarrowing(payload);
      if (narrowingError) return narrowingError;
      return buildBindablesManifest(getSdInstance(), payload);
    },

    ui_structure_set_property_binding: (input) => {
      // Resolve @alias / $N refs from the most recent ui_render so
      // render-expanded binding calls reach the real kuikaId. Same
      // pattern as the other set_*_binding handlers in this file.
      return setDesignerPropertyBinding(getSdInstance(), { ...input, kuikaId: resolveKuikaIdRef(input.kuikaId) });
    },

    ui_logic_set_property_symbol: (input) => {
      const parsedSymbol = safeParse(input.symbol);
      const resolvedKuikaId = resolveKuikaIdRef(input.kuikaId);

      // Detect JSX binding expression strings like "{current.Status}", "{screenInput:id}", "{default:Now}"
      if (typeof parsedSymbol === "string" && /^\{.+\}$/.test(parsedSymbol)) {
        const inner = parsedSymbol.slice(1, -1).trim();
        const lowerInner = inner.toLowerCase();

        if (lowerInner.startsWith("current.")) {
          const fieldName = inner.slice("current.".length).trim();
          return setDesignerPropertyBinding(getSdInstance(), {
            kuikaId: resolvedKuikaId,
            propertyName: input.propertyName,
            bindingType: "actionField",
            fieldName
          });
        }
        if (lowerInner.startsWith("screeninput:")) {
          const colonIdx = inner.indexOf(":");
          const inputName = inner.slice(colonIdx + 1).trim();
          return setDesignerPropertyBinding(getSdInstance(), {
            kuikaId: resolvedKuikaId,
            propertyName: input.propertyName,
            bindingType: "screenInput",
            inputName
          });
        }
        if (lowerInner.startsWith("default.")) {
          // The dot form would otherwise be stored verbatim as a symbol string.
          const dotFormError: IResolvedDefaultValue | { error: string } = resolveDefaultValueName(inner);
          if ("error" in dotFormError) return { success: false, error: dotFormError.error };
        }
        if (lowerInner.startsWith("default:")) {
          const colonIdx = inner.indexOf(":");
          const defaultName = inner.slice(colonIdx + 1).trim();
          return setDesignerPropertyBinding(getSdInstance(), {
            kuikaId: resolvedKuikaId,
            propertyName: input.propertyName,
            bindingType: "default",
            defaultName
          });
        }
      }

      return setDesignerPropertySymbol(getSdInstance(), {
        ...input,
        kuikaId: resolvedKuikaId,
        symbol: parsedSymbol
      } as ISetPropertySymbolInput);
    },

    ui_logic_update_property_symbol: (input) => {
      return updateDesignerPropertySymbol(getSdInstance(), input as IUpdatePropertySymbolInput);
    },

    ui_logic_set_value_binding: (input) => {
      // Fix #8b — friendlyName auto-resolve before alias resolution.
      autoResolveKuikaIdField(input, "kuikaId", getSdInstance());
      autoResolveKuikaIdField(input, "sourceKuikaId", getSdInstance());
      const kuikaId = resolveKuikaIdRef(input.kuikaId);
      const propertyName = input.propertyName || "value";
      const bindingType = input.bindingType;
      if (!kuikaId) return { error: "kuikaId is required" };
      if (!bindingType) return { error: "bindingType is required" };

      return setDesignerPropertyBinding(getSdInstance(), {
        kuikaId,
        propertyName,
        bindingType,
        sourceActionId: input.sourceActionId,
        actionId: input.actionId,
        fieldName: input.fieldName,
        defaultName: input.defaultName,
        inputName: input.inputName,
        sourceKuikaId: input.sourceKuikaId,
        sourcePropertyName: input.sourcePropertyName
      });
    },

    ui_logic_set_property_type: (input) => {
      return setDesignerPropertyType(getSdInstance(), input as ISetPropertyTypeInput);
    },

    ui_logic_set_property_type_options: (input) => {
      return setDesignerPropertyTypeOptions(getSdInstance(), input as ISetPropertyTypeOptionsInput);
    },

    ui_logic_get_property_data_editor: (input) => {
      return getDesignerPropertyDataEditorState(getSdInstance(), input);
    },

    ui_logic_set_property_data_action: (input) => {
      return setDesignerPropertyDataAction(getSdInstance(), input);
    }
  },

  async: {
    /**
     * `ui_bind.get_options` — the sync manifest builder, run only once lazily
     * loaded picker branches have settled so their options are in the manifest.
     */
    ui_bindables_get_settled: async (input) => {
      const settled: boolean = await waitForDynamicTypesSettled();
      const result = propertyTools.sync!.ui_bindables_get(input);
      if (settled || !result || typeof result !== "object" || "error" in result) return result;
      return {
        ...result,
        note: "Bag & Cache result types were still loading after 5s — their options are missing from this manifest. Call get_options again for them."
      };
    },

    ui_bind: async (input) => {
      const bindings = safeParse(input?.bindings);
      if (!Array.isArray(bindings) || bindings.length === 0) {
        return { error: "bindings must be a non-empty array" };
      }

      interface BindingResult {
        kuikaId?: string;
        propertyName?: string;
        success: boolean;
        error?: string;
        actionId?: string;
        message?: string;
      }

      const results: BindingResult[] = [];
      let allSuccess = true;

      let cachedEnrichmentState:
        | { sdState: ReturnType<typeof getValues>; screenDict: ReturnType<typeof getScreenDictionary> }
        | undefined;
      const getEnrichmentState = () => {
        if (!cachedEnrichmentState) {
          const sdState = getValues(getSdInstance());
          cachedEnrichmentState = { sdState, screenDict: getScreenDictionary(sdState) };
        }
        return cachedEnrichmentState;
      };

      for (const raw of bindings) {
        const binding = safeParse(raw) ?? {};
        // Fix #8b — auto-resolve friendlyName / id-alias kuikaId for
        // ui_bind.apply. Same rationale as Fix #8 on ui_action.* — the
        // Binder LLM consistently passes `kuikaId: "input-title"`
        // (friendlyName) where the dictionary keys by numeric id.
        autoResolveKuikaIdField(binding, "kuikaId", getSdInstance());
        // Fix #34 — rewrite `componentValue:<friendlyName>[:propName]`
        // shorthand into the canonical `di:<numericKuikaId>:<propName>`
        // shape before any decoder runs. See the helper docstring.
        if (typeof binding.symbolRef === "string") {
          const rewritten: string | undefined = rewriteComponentValueSymbolRef(binding.symbolRef, getSdInstance());
          if (rewritten !== undefined) {
            binding.symbolRef = rewritten;
          }
        }
        const { kuikaId, propertyName } = binding;
        const base: BindingResult = { kuikaId, propertyName, success: false };

        if (!kuikaId || !propertyName) {
          results.push({ ...base, error: "kuikaId and propertyName are required" });
          allSuccess = false;
          continue;
        }

        const hasSymbolRef = typeof binding.symbolRef === "string" && binding.symbolRef.length > 0;
        const hasLiteral = Object.prototype.hasOwnProperty.call(binding, "literal");

        if (!hasSymbolRef && !hasLiteral) {
          results.push({ ...base, error: "symbolRef or literal is required" });
          allSuccess = false;
          continue;
        }

        // Event attach path: `actionDef:` symbolRef means "instantiate this action on this event".
        if (hasSymbolRef && isActionDefBinding(binding.symbolRef)) {
          try {
            const decoded = decodeSymbolRef(binding.symbolRef);
            if (decoded.kind !== "actionDef") {
              throw new SymbolResolutionError("expected actionDef ref");
            }
            const attachPayload: Record<string, unknown> = {
              kuikaId,
              eventName: propertyName,
              actionDefinitionId: decoded.actionDefinitionId,
              parameters: translateEventParameters(binding.parameters)
            };
            const attachResult = await actionTools.async!.ui_logic_add_action_to_event(attachPayload);
            if (attachResult && "error" in attachResult && attachResult.error) {
              results.push({ ...base, error: String(attachResult.error) });
              allSuccess = false;
            } else {
              const resultObj = (attachResult as { actionId?: string; message?: string }) || {};
              results.push({
                ...base,
                success: true,
                actionId: resultObj.actionId,
                message: resultObj.message
              });
            }
          } catch (e) {
            results.push({ ...base, error: (e as Error).message });
            allSuccess = false;
          }
          continue;
        }

        // Value binding path: resolve to ISymbol and apply.
        try {
          let symbol: ISymbol;
          if (hasSymbolRef) {
            // `default.X` is not a symbolRef kind; say what IS rather than "unknown kind".
            if (/^default\./i.test(binding.symbolRef)) {
              const dotForm: IResolvedDefaultValue | { error: string } = resolveDefaultValueName(binding.symbolRef);
              if ("error" in dotForm) throw new SymbolResolutionError(dotForm.error);
            }
            symbol = resolveSymbolFromRef(binding.symbolRef);
            // A `default:` ref must name a REAL default. An unknown name used to be
            // stored verbatim — success for a binding codegen emits as plain text.
            // The registry key (`default:GetMyUsername`) is accepted as an alias of
            // the label form (`default:Current Username`) and persisted canonically.
            if (symbol.category === ESymbolCategory.default) {
              const resolvedDefault: IResolvedDefaultValue | { error: string } = resolveDefaultValueName(
                symbol.defaultValueName ?? ""
              );
              if ("error" in resolvedDefault) throw new SymbolResolutionError(resolvedDefault.error);
              symbol = {
                ...symbol,
                defaultValueName: resolvedDefault.defaultValueName,
                currentType: resolvedDefault.type,
                targetType: resolvedDefault.type
              } as ISymbol;
            }
            // KOA-17797 (BN-01) — an `actionResult:` / `actionField:` value
            // binding points at an action INSTANCE wired on the screen; its
            // `resultActionId` is that instance's id (a GUID). When it resolves
            // to no on-screen instance (e.g. the planner emits
            // `actionResult:GetAllUsers` — a NAME, not an instance id — on a
            // project that has no such action), the ref is a PHANTOM: without
            // this guard `setDesignerPropertySymbol` still returns success and
            // leaves an empty/broken binding the user sees as "olmadı". Refuse
            // honestly instead. Skip when there is no action-definition context
            // to validate against (can't confirm → don't block); real Binder
            // binds carry a live instance GUID and resolve, so they pass.
            const isActionInstanceRef: boolean =
              symbol.category === ESymbolCategory.actionResults || symbol.category === ESymbolCategory.actionField;
            let resolvedActionDefinition: IActionDefinitionDto | undefined;
            if (isActionInstanceRef) {
              const { sdState, screenDict } = getEnrichmentState();
              const actionDefinitions: readonly IActionDefinitionDto[] = sdState.actionDefinitions ?? [];
              resolvedActionDefinition = symbol.resultActionId
                ? findActionDefinitionByResultActionId(screenDict, actionDefinitions, symbol.resultActionId)
                : undefined;
              const canValidate: boolean = actionDefinitions.length > 0 && Object.keys(screenDict).length > 0;
              if (canValidate && resolvedActionDefinition === undefined) {
                results.push({
                  ...base,
                  error:
                    `No action wired on this screen resolves to "${symbol.resultActionId ?? binding.symbolRef}". ` +
                    `Bind to the result of an action that is attached on this screen (e.g. a GetAll action on ` +
                    `pageInit), or wire that action first — the reference does not exist, so the binding would be empty.`
                });
                allSuccess = false;
                continue;
              }
            }
            // Upgrade flat `actionField:<id>:<field>` symbols to the
            // `fieldOfType` shape the picker produces. See
            // bindables/action-field-enricher.ts. Reuses the already-resolved
            // definition so we don't look it up twice.
            if (symbol.category === ESymbolCategory.actionField) {
              const { screenDict } = getEnrichmentState();
              const targetShape = findTargetPropertyShape(screenDict, kuikaId, propertyName);
              symbol = enrichActionFieldSymbol(symbol, {
                actionDefinition: resolvedActionDefinition,
                targetType: targetShape?.targetType,
                targetCardinality: targetShape?.targetCardinality
              });
            } else if (symbol.category === ESymbolCategory.actionResults) {
              // A whole-result (`actionResult:<id>`) bind onto a scalar DISPLAY
              // target (e.g. Label.value) should resolve to a FIELD, not the raw
              // action result — a Label shows one value, not a result object. The
              // small-task `bind` path loses the user's named field (only the
              // instance id survives), so default to the action's FIRST return
              // field via the same fieldOfType enricher the human picker uses.
              // Guarded to row/object-returning actions bound to a scalar target:
              // a genuinely scalar action (no return fields) OR a LIST target
              // (e.g. Table.options) keeps the whole-result binding.
              const { screenDict } = getEnrichmentState();
              const targetShape = findTargetPropertyShape(screenDict, kuikaId, propertyName);
              const firstField = resolvedActionDefinition?.returnTypeModel?.elements?.[0];
              if (firstField?.name && targetShape?.targetCardinality === ECardinality.One) {
                const fieldBase: ISymbol = {
                  ...symbol,
                  category: ESymbolCategory.actionField,
                  fieldName: firstField.name
                };
                symbol = enrichActionFieldSymbol(fieldBase, {
                  actionDefinition: resolvedActionDefinition,
                  targetType: targetShape.targetType,
                  targetCardinality: targetShape.targetCardinality
                });
              }
            }
          } else {
            symbol = resolveSymbolFromLiteral(binding.literal);
          }
          const applied = setDesignerPropertySymbol(getSdInstance(), {
            kuikaId,
            propertyName,
            symbol
          });
          if (applied && "error" in applied) {
            results.push({ ...base, error: String(applied.error) });
            allSuccess = false;
          } else {
            results.push({
              ...base,
              success: true,
              message: (applied as { message?: string })?.message
            });
          }
        } catch (e) {
          results.push({ ...base, error: (e as Error).message });
          allSuccess = false;
        }
      }

      return { success: allSuccess, count: results.length, results };
    },

    ui_logic_list_microapp_projects: async () => {
      return listDesignerMicroAppProjects(getSdInstance());
    },

    ui_logic_list_microapp_project_screens: async (input) => {
      return listDesignerMicroAppProjectScreens(getSdInstance(), input);
    },

    ui_logic_list_microapp_screen_inputs: async (input) => {
      return listDesignerMicroAppScreenInputs(getSdInstance(), input);
    },

    ui_logic_set_microapp_project: async (input) => {
      return setDesignerMicroAppProject(getSdInstance(), input as ISetMicroAppProjectInput);
    },

    ui_logic_set_microapp_starting_screen: async (input) => {
      return setDesignerMicroAppStartingScreen(getSdInstance(), input as ISetMicroAppStartingScreenInput);
    },

    ui_logic_set_property_data_field: async (input) => {
      // Resolve @alias / $N refs before reaching the SD instance — without this
      // a SelectBox added in the same ui_render batch (alias `@_cN`) could not
      // receive its `options` payload because the SD lookup expects a real
      // numeric kuikaId. Mirrors the resolution every other property handler
      // in this file does (KOA-17240 — SelectBox `options=` was lost because
      // the alias chain broke for fixedValue writes).
      return setDesignerPropertyDataField(getSdInstance(), {
        ...input,
        kuikaId: resolveKuikaIdRef(input.kuikaId),
        value: safeParse(input.value)
      });
    },

    ui_logic_set_multiple_bindings: async (input) => {
      const bindings = safeParse(input.bindings);
      if (!bindings || !Array.isArray(bindings) || bindings.length === 0) {
        return { error: "bindings array is required" };
      }

      const results: any[] = [];
      let allSuccess = true;

      for (const binding of bindings) {
        // Fix #8b — friendlyName auto-resolve before alias resolution.
        autoResolveKuikaIdField(binding, "kuikaId", getSdInstance());
        autoResolveKuikaIdField(binding, "sourceKuikaId", getSdInstance());
        const resolvedKuikaId = resolveKuikaIdRef(binding.kuikaId);
        const resolvedBinding = { ...binding, kuikaId: resolvedKuikaId };
        try {
          const result = setDesignerPropertyBinding(getSdInstance(), resolvedBinding);
          if (result && "error" in result) {
            allSuccess = false;
            results.push({ kuikaId: resolvedKuikaId, success: false, error: (result as any).error });
          } else {
            results.push({ kuikaId: resolvedKuikaId, success: true, message: (result as any)?.message });
          }
        } catch (e: any) {
          allSuccess = false;
          results.push({ kuikaId: resolvedKuikaId, success: false, error: e.message });
        }
      }

      return { success: allSuccess, count: results.length, results };
    },

    ui_logic_set_multiple_data_fields: async (input) => {
      const bindings = safeParse(input.bindings);
      if (!bindings || !Array.isArray(bindings) || bindings.length === 0) {
        return { error: "bindings array is required" };
      }

      const results: any[] = [];
      let allSuccess = true;

      for (const binding of bindings) {
        // Fix #8b — friendlyName / id-alias auto-resolve in addition to
        // the existing @alias / $N resolution. The Binder LLM passes
        // kebab-case ids ("input-channel") here too; without this we
        // get "Component with kuikaId 'input-channel' not found".
        autoResolveKuikaIdField(binding, "kuikaId", getSdInstance());
        // Resolve @alias / $N refs to real kuikaIds — required when the
        // bound component was added in the same ui_render batch (e.g. a
        // fresh SelectBox carrying `options=[...]`). Mirror of the
        // resolution `set_multiple_bindings` already does at line 411.
        // Without this, fixedValue writes for SelectBox / MultiSelect /
        // AutoComplete / RadioGroup all reported "Component with kuikaId
        // @_cN not found" and the dropdown rendered with default
        // placeholders only (KOA-17240 contact-form repro).
        const resolvedKuikaId = resolveKuikaIdRef(binding.kuikaId);
        try {
          const result = await setDesignerPropertyDataField(getSdInstance(), {
            kuikaId: resolvedKuikaId,
            propertyName: binding.propertyName,
            key: binding.key,
            value: safeParse(binding.value)
          });
          results.push({ kuikaId: resolvedKuikaId, success: true, ...result });
        } catch (e: any) {
          allSuccess = false;
          results.push({ kuikaId: resolvedKuikaId, success: false, error: e.message });
        }
      }

      return { success: allSuccess, results };
    },

    /**
     * Composite handler: atomically sets datasource on a component,
     * resolves the action instance ID, and applies child bindings.
     * Eliminates the multi-round set_data_action → set_multiple_bindings dependency.
     */
    ui_logic_set_datasource_bindings: async (input) => {
      const datasource = safeParse(input.datasource);
      const bindings = safeParse(input.bindings) || [];

      if (!datasource?.kuikaId || !datasource?.actionName) {
        return { error: "datasource.kuikaId and datasource.actionName are required" };
      }

      // Resolve @alias / $N refs emitted by the render translator — the
      // repeater is usually added in the same ui_render batch and only
      // exists under its alias until `_mcpLastAliasMap` is consulted.
      const resolvedDatasourceKuikaId = resolveKuikaIdRef(datasource.kuikaId);
      const datasourcePropertyName = datasource.propertyName || "dataSource";

      // 1. Set datasource action on parent component
      const dsResult = setDesignerPropertyDataAction(getSdInstance(), {
        kuikaId: resolvedDatasourceKuikaId,
        propertyName: datasourcePropertyName,
        value: datasource.actionName
      });
      if (dsResult && "error" in dsResult && !(dsResult as any).success) {
        return dsResult;
      }

      // 2. Field mappings. None of these is fatal — the action binding in step 1
      // already succeeded — but a mapping that did not land is REPORTED in
      // `warnings`, never swallowed: a silently dropped dataTextField is a
      // dropdown that renders blank with nothing to explain why.
      //
      // Order matters: all of them run AFTER step 1, because
      // setDesignerPropertyDataAction clears dependent fields when the action
      // changes — set earlier they would be wiped.
      //   - dataTextField / dataValueField → SelectBox-style option columns
      //   - chartRoles → flat basic-chart data roles (chartDataDisplayLabel,
      //     chartFirstDataField, …); `datasource` is already parsed above, so
      //     this is a live array (the WS bridge sends it verbatim)
      //   - fieldMaps → options-bag maps with DOTTED keys
      //     (`KanbanBoardCardOptions.cardId`), routed through
      //     setNestedOptionValue to write `symbol.<Group>.<field>`
      const fieldWrites: IDatasourceFieldWrite[] = [];
      if (datasource.dataTextField) {
        fieldWrites.push({ source: "dataTextField", key: "fieldToDisplay", value: String(datasource.dataTextField) });
      }
      if (datasource.dataValueField) {
        fieldWrites.push({
          source: "dataValueField",
          key: "fieldToUseAsKey",
          value: String(datasource.dataValueField)
        });
      }
      fieldWrites.push(...collectKeyedFieldWrites("chartRoles", datasource.chartRoles));
      fieldWrites.push(...collectKeyedFieldWrites("fieldMaps", datasource.fieldMaps));
      const warnings: string[] = await applyDatasourceFieldWrites(
        resolvedDatasourceKuikaId,
        datasourcePropertyName,
        fieldWrites
      );

      // 3. Resolve action instance ID from configured symbol
      let resolvedActionId: string | undefined;
      try {
        const sd = getValues(getSdInstance());
        const dict = getScreenDictionary(sd);
        const parentDi = dict[parseInt(resolvedDatasourceKuikaId, 10)];
        const prop = parentDi?.item?.properties?.find((p: any) => p.name === datasourcePropertyName);
        resolvedActionId = prop?.symbol?.resultActionId;
      } catch {
        // If resolution fails, bindings will be attempted without actionId
      }

      // Fix #35 — Race-free instance bootstrap.
      //
      // `setDesignerPropertyDataAction` resolves the action by NAME via
      // `getDefinedActions`, which only returns action INSTANCES already
      // attached to some event on the screen. On a freshly-rendered
      // Table (the Designer just emitted `<Table datasource="Ideas_GetAll">`
      // before the Wirer has had a chance to attach `Ideas_GetAll` to
      // `pageInit`), no instance exists yet → `symbol.resultActionId`
      // is undefined → every child `{current.X}` binding fails with
      // "actionId or sourceActionId is required for 'actionField'
      // bindingType". Verified in the May 2026 fikir-havuzu trace
      // (Step 10) where 5 cell bindings failed identically and the
      // verifier retry didn't repair (same race, same result).
      //
      // Resolution: when step 3 returns no `resultActionId` AND the
      // call has child bindings that need one, attach the action to
      // the screen's `pageInit` ourselves (canonical home for
      // datasource queries), then re-run the parent symbol write so
      // `resolveActionSelection` finds the freshly-created instance.
      // The Wirer's later `add_to_event` for the same action hits the
      // existing dedup ("alreadyExists: true — skipped") and the
      // outcome stays clean. Built-in NAVIGATE / GoBack / IfThenElse /
      // Notify etc. are not legitimate datasource actions, so we don't
      // need to filter — only user-defined queries land here, and they
      // belong on `pageInit` either way.
      //
      // We gate on `bindings.length > 0` so options-only datasources
      // (SelectBox with just dataTextField/dataValueField, no
      // `actionField:` cell bindings) don't get a redundant pageInit
      // pin from this path — those rely on the Wirer's explicit
      // datasource attachment from the brief.
      const hasChildBindings: boolean = Array.isArray(bindings) && bindings.length > 0;
      if (!resolvedActionId && hasChildBindings) {
        // KOA-17887 Faz D — the datasource name may be a dotted list row source
        // (`ACTION.LISTFIELD`, e.g. `BAPI_..._GET_LIST.EMPLOYEE_DATA`). Resolve/attach
        // the ACTION by its BASE name — the suffix is a field path, not part of the
        // action name. Passing the raw dotted string matched no action definition, so
        // the instance never linked, `resolvedActionId` stayed undefined, and every
        // `{current.FIELD}` cell binding failed with "actionId required" (session
        // 963cd763). The table symbol write below still uses the full dotted path so
        // `resolveActionSelection` descends into the list item type.
        const baseActionName: string = String(datasource.actionName).split(/[.#]/)[0];
        // KOA-17564 — choose WHERE to bootstrap the instance.
        //
        // Default home is the screen's `pageInit`: a paramless query
        // (GetAll / lookup) must auto-load when the screen opens. BUT a
        // PARAMETERIZED query whose values come from on-screen inputs (a
        // filter / search) must NOT run at page load — its parameters are
        // empty then, so the platform errors and the load is meaningless.
        // When such an action has a screen-level trigger button (the
        // contract-id directive emits one as `btn-<ActionName>` for every
        // wired button), bootstrap the instance on THAT button's `onClick`
        // instead of `pageInit`. The table's `{current.X}` cell bindings
        // resolve to it (resolveActionSelection is event-agnostic), the
        // Wirer's later attach of the same action to the same button dedups
        // to this instance, and no empty-parameter pageInit copy is ever
        // created. Paramless actions, or parameterized ones with no trigger
        // button, keep the legacy `pageInit` home (graceful fallback).
        let bootstrapKuikaId = "-1";
        let bootstrapEvent = "pageInit";
        try {
          const sdForTarget = getValues(getSdInstance());
          const actionDef = getAllActionDefinitions(sdForTarget).find(
            (d) => (d.name || "").toLowerCase() === baseActionName.toLowerCase()
          );
          const isParameterized: boolean =
            (Array.isArray(actionDef?.parameters) && actionDef!.parameters.length > 0) ||
            (Array.isArray(actionDef?.actionParameters) && actionDef!.actionParameters.length > 0);
          if (isParameterized) {
            const triggerRef: string = `btn-${baseActionName}`;
            const resolvedTrigger: string | undefined = resolveFriendlyKuikaId(triggerRef, getSdInstance());
            if (resolvedTrigger && /^\d+$/.test(resolvedTrigger)) {
              bootstrapKuikaId = resolvedTrigger;
              bootstrapEvent = "onClick";
            }
          }
        } catch {
          // Resolution failed — fall back to the pageInit home below.
        }
        try {
          const ensureResult = actionTools.sync!.ui_logic_add_action_to_event({
            kuikaId: bootstrapKuikaId,
            eventName: bootstrapEvent,
            actionDefinitionId: baseActionName
          });
          const ensureOk: boolean =
            (ensureResult as { success?: boolean })?.success === true ||
            (ensureResult as { alreadyExists?: boolean })?.alreadyExists === true;
          if (ensureOk) {
            // Re-run the parent symbol write so resolveActionSelection
            // finds the now-existing instance and updates resultActionId.
            setDesignerPropertyDataAction(getSdInstance(), {
              kuikaId: resolvedDatasourceKuikaId,
              propertyName: datasourcePropertyName,
              value: datasource.actionName
            });
            const sd = getValues(getSdInstance());
            const dict = getScreenDictionary(sd);
            const parentDi = dict[parseInt(resolvedDatasourceKuikaId, 10)];
            const prop = parentDi?.item?.properties?.find((p: any) => p.name === datasourcePropertyName);
            resolvedActionId = prop?.symbol?.resultActionId;

            // KOA-17362 (F1) — system/platform actions (e.g. the `GetUserTasks`
            // task feed on a process task-inbox) bind as a table datasource just
            // like a custom SQL `_GetAll`, but the parent-symbol re-resolution
            // above (resolveActionSelection) resolves USER datasource actions
            // first-class and can leave `resultActionId` unset for a system
            // action's freshly-attached instance. The `add_to_event` call above
            // DEFINITIVELY created that instance and returned its id — use it as
            // the authoritative fallback so the `{current.X}` cell bindings get a
            // real actionId instead of failing with "actionId is required for
            // 'actionField' bindingType" (the task-inbox then renders live tasks
            // instead of the leftover sample rows). Additive: only fires when the
            // standard resolution produced nothing, so the custom-action path is
            // byte-for-byte unchanged.
            if (!resolvedActionId) {
              const ensuredActionId: string | undefined = (ensureResult as { actionId?: string })?.actionId;
              if (typeof ensuredActionId === "string" && ensuredActionId.length > 0) {
                resolvedActionId = ensuredActionId;

                // KOA-17362 (F1, parent-symbol) — the child `{current.X}` cell
                // bindings now get the real actionId via `resolvedActionId`, but
                // the PARENT dataSource symbol is STILL unbound: the
                // `setDesignerPropertyDataAction` write above went through
                // `resolveActionSelection`, which links a USER datasource action's
                // instance by exact label and CANNOT link a platform action's
                // fresh instance, so it left `resultActionId` (and often
                // `actionDefinitionId`) undefined on the parent. A dataSource
                // symbol with no `resultActionId` is not bound to any action
                // result → the component falls back to its DEFAULT MOCK rows (the
                // task-inbox showed sample data). Write the authoritative instance
                // id straight onto the parent symbol so the datasource binds to
                // the real action result. General + action-agnostic: keys off the
                // returned instance id (never a hard-coded name or locale), and is
                // additive — the whole branch only runs when standard resolution
                // produced no `resultActionId`, so custom `_GetAll` datasources are
                // byte-for-byte unchanged.
                try {
                  const parentSymbol: ISymbol | undefined = prop?.symbol ? ({ ...prop.symbol } as ISymbol) : undefined;
                  if (parentSymbol) {
                    const instance: { id?: string; actionDefinitionId?: string } | undefined = sd
                      ?.getDefinedActions?.()
                      .find((a: { id?: string }) => a.id === resolvedActionId);
                    parentSymbol.isFixed = false;
                    parentSymbol.resultActionId = resolvedActionId;
                    if (!parentSymbol.actionDefinitionObjectPath) {
                      parentSymbol.actionDefinitionObjectPath = String(datasource.actionName);
                    }
                    if (!parentSymbol.actionDefinitionId && instance?.actionDefinitionId) {
                      parentSymbol.actionDefinitionId = instance.actionDefinitionId;
                    }
                    setDesignerPropertySymbol(getSdInstance(), {
                      kuikaId: resolvedDatasourceKuikaId,
                      propertyName: datasourcePropertyName,
                      symbol: parentSymbol
                    });
                  }
                } catch {
                  // Best-effort: a parent-symbol backfill failure leaves the child
                  // bindings' actionId fix intact rather than aborting the whole
                  // datasource-binding call.
                }
              }
            }
          }
        } catch {
          // Bootstrap failed — fall through and let child bindings
          // surface the original "actionId is required" error so the
          // gap is visible upstream rather than swallowed.
        }
      }

      // 4. Apply child bindings with resolved actionId
      if (Array.isArray(bindings) && bindings.length > 0) {
        const resolvedBindings = bindings.map((b: any) => ({
          ...b,
          kuikaId: resolveKuikaIdRef(b.kuikaId),
          actionId: b._needsParentActionId && resolvedActionId ? resolvedActionId : b.actionId,
          _needsParentActionId: undefined
        }));

        const bindResult = await propertyTools.async!.ui_logic_set_multiple_bindings({
          bindings: resolvedBindings
        });
        // KOA-17797 — upgrade each row-column display binding to the deterministic
        // field-of-type symbol the human column picker produces, so an AI-bound
        // table column matches a hand-bound one (fieldToDisplay/fieldType/fixedValue,
        // corrected casing). Runs after the flat symbols are applied above.
        enrichDatasourceColumnBindings(resolvedBindings as IDatasourceCellBinding[]);
        return {
          success: true,
          datasource: datasource.actionName,
          actionId: resolvedActionId,
          bindingResults: bindResult,
          ...(warnings.length > 0 ? { warnings } : {})
        };
      }

      return {
        success: true,
        datasource: datasource.actionName,
        actionId: resolvedActionId,
        ...(warnings.length > 0 ? { warnings } : {})
      };
    }
  }
};
